/**
 * ExecutorRiskProfilePage — 7-question CICEA estate risk assessment wizard.
 * Route: /executor/:clientId/risk-profile
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ChevronRight, AlertTriangle, CheckCircle2, AlertCircle } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface Answer { questionIndex: number; answer: "yes" | "maybe" | "no"; }

interface RiskProfile {
  id: string;
  clientId: string;
  answers: Answer[];
  totalScore: number;
  riskLevel: "low" | "moderate" | "significant";
  completedAt: string | null;
  updatedAt: string;
}

const QUESTIONS = [
  {
    index: 0,
    shortLabel: "Family dynamics",
    question: "Does the family include any of the following: a blended family or remarriage, strained relationships between family members, or beneficiaries dealing with financial difficulty, health challenges, or addiction?",
  },
  {
    index: 1,
    shortLabel: "Unequal treatment",
    question: "Could any beneficiary feel they were treated unfairly — for example, through uneven inheritance, exclusion from heirlooms, or lack of recognition for providing care to the deceased?",
  },
  {
    index: 2,
    shortLabel: "Executor complexity",
    question: "Are there multiple executors, and if so, could there be disagreements between them about how to handle the estate — including decisions about hiring professionals or managing assets?",
  },
  {
    index: 3,
    shortLabel: "Financial complexity",
    question: "Does the estate include complex financial holdings such as business interests, outstanding loans to family members, or charitable gifts that require special handling?",
  },
  {
    index: 4,
    shortLabel: "Will quality",
    question: "Is there any concern about the Will itself — for example, was it prepared without a lawyer, are the instructions ambiguous, or might any beneficiary be surprised by its contents?",
  },
  {
    index: 5,
    shortLabel: "Emotionally charged assets",
    question: "Are there assets with strong emotional significance — such as a family cottage, heirlooms, or a home where a beneficiary currently lives — that could cause disagreements during distribution?",
  },
  {
    index: 6,
    shortLabel: "Business interests",
    question: "Does the estate include a business, and if so, is there a clear plan for what happens to it — including who takes over, how it's funded, and whether that aligns with the Will?",
  },
];

const SCORE_MAP: Record<string, number> = { yes: 2, maybe: 1, no: 0 };

function getRiskConfig(level: "low" | "moderate" | "significant") {
  if (level === "low") return {
    color: "text-green-700",
    bg: "bg-green-50 border-green-200",
    icon: CheckCircle2,
    iconColor: "text-green-600",
    label: "Low Risk",
    recommendation: "This estate has a low risk profile. Standard executor duties apply.",
  };
  if (level === "moderate") return {
    color: "text-orange-700",
    bg: "bg-orange-50 border-orange-200",
    icon: AlertCircle,
    iconColor: "text-orange-600",
    label: "Moderate Risk",
    recommendation: "This estate has a moderate risk profile. Consider consulting your estate lawyer about potential complications.",
  };
  return {
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
    icon: AlertTriangle,
    iconColor: "text-red-600",
    label: "Significant Risk",
    recommendation: "This estate has a significant risk profile. Strongly consider obtaining Executor Insurance (E&O coverage) to protect yourself and your family. Consult your estate lawyer before proceeding.",
  };
}

interface Props { clientId: string; }

export default function ExecutorRiskProfilePage({ clientId }: Props) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [step, setStep] = useState<number | "results">(-1);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const { data: profile, isLoading } = useQuery<RiskProfile | null>({
    queryKey: [`/api/clients/${clientId}/risk-profile`],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/risk-profile`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: Infinity,
  });

  const { data: executors = [] } = useQuery<any[]>({
    queryKey: [`/api/clients/${clientId}/executors`],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/executors`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isLoading) return;
    if (profile && step === -1) {
      setAnswers(profile.answers);
      setStep("results");
    }
  }, [profile, isLoading]);

  const saveMutation = useMutation({
    mutationFn: async (finalAnswers: Answer[]) => {
      const res = await fetch(`/api/clients/${clientId}/risk-profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ answers: finalAnswers }),
      });
      if (!res.ok) throw new Error("Failed to save risk profile");
      return res.json() as Promise<RiskProfile>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/risk-profile`] });
    },
    onError: () => {
      toast({ title: "Save failed", description: "Could not save your assessment.", variant: "destructive" });
    },
  });

  function getAutoHint(questionIndex: number): boolean {
    if (questionIndex === 2 && Array.isArray(executors) && executors.length > 1) return true;
    return false;
  }

  function handleAnswer(answer: "yes" | "maybe" | "no") {
    if (typeof step !== "number") return;
    const newAnswers = [...answers.filter(a => a.questionIndex !== step), { questionIndex: step, answer }];
    setAnswers(newAnswers);

    if (step < 6) {
      setStep(step + 1);
    } else {
      saveMutation.mutate(newAnswers, {
        onSuccess: () => {
          setStep("results");
        },
      });
    }
  }

  function handleRetake() {
    setAnswers([]);
    setStep(0);
  }

  function computeResults(ans: Answer[]) {
    const totalScore = ans.reduce((sum, a) => sum + (SCORE_MAP[a.answer] ?? 0), 0);
    const riskLevel: "low" | "moderate" | "significant" =
      totalScore === 0 ? "low" : totalScore <= 3 ? "moderate" : "significant";
    return { totalScore, riskLevel };
  }

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }

  // ── Intro screen ─────────────────────────────────────────────────────────────
  if (step === -1) {
    return (
      <div className="max-w-xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={() => setLocation(`/executor/${clientId}/dashboard`)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> Back to your plan
        </button>
        <div>
          <h1 className="text-2xl font-bold">Estate Risk Profile</h1>
          <p className="text-muted-foreground mt-1">
            A 7-question assessment based on the CICEA Executor Information Form. Takes about 2 minutes.
          </p>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Answer each question based on what you know about the estate. Your answers help determine the complexity
              and risk level so you can decide whether to seek professional help.
            </p>
            <p className="text-sm text-muted-foreground">
              Scoring: <strong>Yes = 2</strong>, <strong>Maybe = 1</strong>, <strong>No = 0</strong> — total out of 14.
            </p>
            <Button className="w-full" onClick={() => setStep(0)}>
              Start Assessment <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Question screens ──────────────────────────────────────────────────────────
  if (typeof step === "number" && step >= 0 && step <= 6) {
    const q = QUESTIONS[step];
    const autoHint = getAutoHint(step);
    const currentAnswer = answers.find(a => a.questionIndex === step)?.answer;

    return (
      <div className="max-w-xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={() => step > 0 ? setStep(step - 1) : setStep(-1)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> {step > 0 ? "Back" : "Cancel"}
        </button>

        <div className="flex items-center gap-2">
          {QUESTIONS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i < step ? "bg-primary" : i === step ? "bg-primary/60" : "bg-muted"
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Question {step + 1} of 7</p>

        <div>
          <h2 className="text-lg font-semibold">{q.shortLabel}</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{q.question}</p>
        </div>

        {autoHint && (
          <p className="text-xs text-primary bg-primary/5 border border-primary/20 rounded px-3 py-2">
            Based on estate plan data, this may apply. You can override below.
          </p>
        )}

        <div className="grid grid-cols-3 gap-3">
          {(["yes", "maybe", "no"] as const).map((val) => (
            <Button
              key={val}
              variant={currentAnswer === val ? "default" : "outline"}
              className="h-12 capitalize"
              onClick={() => handleAnswer(val)}
              disabled={saveMutation.isPending}
            >
              {val === "yes" ? "Yes" : val === "maybe" ? "Maybe" : "No"}
            </Button>
          ))}
        </div>

        {step === 6 && saveMutation.isPending && (
          <p className="text-sm text-muted-foreground text-center">Saving…</p>
        )}
      </div>
    );
  }

  // ── Results screen ────────────────────────────────────────────────────────────
  if (step === "results") {
    const { totalScore, riskLevel } = computeResults(answers);
    const config = getRiskConfig(riskLevel);
    const Icon = config.icon;

    return (
      <div className="max-w-xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={() => setLocation(`/executor/${clientId}/dashboard`)}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> Back to your plan
        </button>
        <h1 className="text-2xl font-bold">Risk Assessment Results</h1>

        <Card className={`border ${config.bg}`}>
          <CardContent className="p-5 flex items-start gap-4">
            <Icon className={`w-8 h-8 mt-0.5 shrink-0 ${config.iconColor}`} />
            <div>
              <p className={`text-xl font-bold ${config.color}`}>{config.label}</p>
              <p className="text-sm text-muted-foreground">Score: {totalScore} / 14</p>
              <p className="text-sm mt-2 leading-relaxed">{config.recommendation}</p>
              {riskLevel === "significant" && (
                <p className="text-sm mt-2 text-red-700">
                  Check your Legal section for your estate lawyer's contact information.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Breakdown</h2>
          {QUESTIONS.map((q) => {
            const ans = answers.find(a => a.questionIndex === q.index);
            const pts = ans ? SCORE_MAP[ans.answer] : 0;
            return (
              <div key={q.index} className="flex items-center justify-between py-2 border-b last:border-0">
                <p className="text-sm font-medium">{q.shortLabel}</p>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="outline" className="capitalize text-xs">
                    {ans?.answer ?? "—"}
                  </Badge>
                  <span className="text-sm text-muted-foreground w-8 text-right">{pts} pt{pts !== 1 ? "s" : ""}</span>
                </div>
              </div>
            );
          })}
        </div>

        <Button variant="outline" onClick={handleRetake} className="w-full">
          Retake Assessment
        </Button>
      </div>
    );
  }

  return null;
}
